/**
 * Cyrillic → Latin transliteration for Russian, Ukrainian, Belarusian and Bulgarian.
 *
 * Every table below was transcribed from its primary source and checked letter by letter:
 *
 * - ALA-LC — Library of Congress romanization tables (loc.gov/catdir/cpso/roman.html):
 *   Russian (2012), Ukrainian (current table, ligature notes 1–2), Belarusian (2013),
 *   Bulgarian (2013). The tie bar is U+0361; the soft and hard signs are U+02B9 and U+02BA.
 *   The tables' upper-case column capitalises both halves of a tied pair (Ц → T͡S, Я → I͡A)
 *   but only the first letter of a plain digraph (Ж → Zh), which is what `upper` records.
 * - BGN/PCGN — the US/UK names boards' agreements as published by the UK government
 *   (gov.uk/government/publications/romanization-systems): Russian "1947 System" (checked
 *   November 2022), Ukrainian "2019 Agreement" (= Ukraine's national system of 2010),
 *   Belarusian "1979 System" (checked February 2022), Bulgarian "2013 Agreement" (= the
 *   Bulgarian national system of 2009). The optional middle dots of the 1947 Russian
 *   system's note 3 (t·s, sh·ch) are not applied: the board's own gazetteer forms omit them
 *   (Bratsk, not Brat·sk).
 * - ISO 9:1995 — read from GOST 7.79-2000, System A, Table 1, which the standard declares
 *   "the authentic text of ISO 9-95". One Latin letter per Cyrillic letter for every
 *   language, which is what makes it reversible.
 * - Scholarly (scientific) transliteration — Ukrainian from the Harvard Ukrainian Research
 *   Institute's Publications Guidelines (June 2020) "Linguistic/scholarly" column, which
 *   also gives the Russian and Belarusian extras (э → è, х → x); Bulgarian from the
 *   convention of Comrie & Corbett, *The Slavonic Languages* (1993), as tabulated in
 *   Wikipedia's "Scientific transliteration of Cyrillic" (ъ → ă, щ → št).
 * - ICAO — Doc 9303, *Machine Readable Travel Documents*, Part 3, 8th ed. (2021), §6 B.
 *   Russia's Federal Migration Service Order No. 320 (Rossiyskaya Gazeta, 27 March 2013)
 *   requires it for names in international passports.
 */

export type CyrillicLanguage = "ru" | "uk" | "be" | "bg";
export type CyrillicSystem = "ala-lc" | "bgn" | "iso9" | "scholarly" | "icao";

/** Why a letter's romanization depends on its position — shown as a note in the letter table. */
export type CyrillicNote =
  | "bgn-ru-ye"
  | "bgn-ru-yo"
  | "initial-y"
  | "dropped"
  | "zgh"
  | "bg-final-ia"
  | "bg-hard-final"
  | "kept"
  | "historical";

interface Entry {
  /** The romanization of the lower-case letter. */
  lat: string;
  /** The capitalised form when it is not simply the first letter upper-cased. */
  upper?: string;
  note?: CyrillicNote;
}

type Table = Record<string, Entry>;

const TIE = "\u0361";
const tie = (a: string, b: string): Entry => ({
  lat: `${a}${TIE}${b}`,
  upper: `${a.toUpperCase()}${TIE}${b.toUpperCase()}`,
});

/** Modifier letter prime and double prime: the soft and hard signs of ALA-LC, ISO 9 and the scholarly system. */
const PRIME = "ʹ";
const DOUBLE_PRIME = "ʺ";
/** BGN/PCGN writes the soft sign ’ (U+2019) and the hard sign ” (U+201D). */
const BGN_SOFT = "’";
const BGN_HARD = "”";

/** Letters every Slavic Cyrillic alphabet shares and every system writes the same way. */
const COMMON: Table = {
  а: { lat: "a" },
  б: { lat: "b" },
  в: { lat: "v" },
  д: { lat: "d" },
  е: { lat: "e" },
  з: { lat: "z" },
  к: { lat: "k" },
  л: { lat: "l" },
  м: { lat: "m" },
  н: { lat: "n" },
  о: { lat: "o" },
  п: { lat: "p" },
  р: { lat: "r" },
  с: { lat: "s" },
  т: { lat: "t" },
  у: { lat: "u" },
  ф: { lat: "f" },
};

const ALA_LC: Record<CyrillicLanguage, Table> = {
  ru: {
    ...COMMON,
    г: { lat: "g" },
    ё: { lat: "ë" },
    ж: { lat: "zh" },
    и: { lat: "i" },
    і: { lat: "ī", note: "historical" },
    й: { lat: "ĭ" },
    х: { lat: "kh" },
    ц: tie("t", "s"),
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "shch" },
    ъ: { lat: DOUBLE_PRIME },
    ы: { lat: "y" },
    ь: { lat: PRIME },
    ѣ: { ...tie("i", "e"), note: "historical" },
    э: { lat: "ė" },
    ю: tie("i", "u"),
    я: tie("i", "a"),
    ѳ: { lat: "ḟ", note: "historical" },
    ѵ: { lat: "ẏ", note: "historical" },
  },
  uk: {
    ...COMMON,
    г: { lat: "h" },
    ґ: { lat: "g" },
    є: tie("i", "e"),
    ж: tie("z", "h"),
    и: { lat: "y" },
    і: { lat: "i" },
    ї: { lat: "ï" },
    й: { lat: "ĭ" },
    х: { lat: "kh" },
    ц: tie("t", "s"),
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "shch" },
    ь: { lat: PRIME },
    ю: tie("i", "u"),
    я: tie("i", "a"),
    "’": { lat: "’", note: "kept" },
  },
  be: {
    ...COMMON,
    г: { lat: "h" },
    ґ: { lat: "g", note: "historical" },
    ё: tie("i", "o"),
    ж: tie("z", "h"),
    и: { lat: "ī", note: "historical" },
    ї: { lat: "ï", note: "historical" },
    і: { lat: "i" },
    й: { lat: "ĭ" },
    ў: { lat: "ŭ" },
    х: { lat: "kh" },
    ц: { lat: "ts" },
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "shch", note: "historical" },
    ъ: { lat: DOUBLE_PRIME, note: "historical" },
    ы: { lat: "y" },
    ь: { lat: PRIME },
    ѣ: { lat: "ě", note: "historical" },
    э: { lat: "ė" },
    ю: tie("i", "u"),
    я: tie("i", "a"),
    "’": { lat: "’", note: "kept" },
  },
  bg: {
    ...COMMON,
    г: { lat: "g" },
    ж: { lat: "zh" },
    и: { lat: "i" },
    й: { lat: "ĭ" },
    х: { lat: "kh" },
    ц: tie("t", "s"),
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "sht" },
    ъ: { lat: "ŭ", note: "bg-hard-final" },
    ь: { lat: PRIME },
    ѣ: { ...tie("i", "e"), note: "historical" },
    ю: tie("i", "u"),
    я: tie("i", "a"),
    ѫ: { lat: "u\u0310", note: "historical" },
  },
};

const BGN: Record<CyrillicLanguage, Table> = {
  ru: {
    ...COMMON,
    г: { lat: "g" },
    е: { lat: "e", note: "bgn-ru-ye" },
    ё: { lat: "ë", note: "bgn-ru-yo" },
    ж: { lat: "zh" },
    и: { lat: "i" },
    й: { lat: "y" },
    х: { lat: "kh" },
    ц: { lat: "ts" },
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "shch" },
    ъ: { lat: BGN_HARD },
    ы: { lat: "y" },
    ь: { lat: BGN_SOFT },
    э: { lat: "e" },
    ю: { lat: "yu" },
    я: { lat: "ya" },
  },
  uk: {
    ...COMMON,
    г: { lat: "h", note: "zgh" },
    ґ: { lat: "g" },
    є: { lat: "ie", note: "initial-y" },
    ж: { lat: "zh" },
    и: { lat: "y" },
    і: { lat: "i" },
    ї: { lat: "i", note: "initial-y" },
    й: { lat: "i", note: "initial-y" },
    х: { lat: "kh" },
    ц: { lat: "ts" },
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "shch" },
    ь: { lat: "", note: "dropped" },
    ю: { lat: "iu", note: "initial-y" },
    я: { lat: "ia", note: "initial-y" },
    "’": { lat: "", note: "dropped" },
  },
  be: {
    ...COMMON,
    г: { lat: "h" },
    е: { lat: "ye" },
    ё: { lat: "yo" },
    ж: { lat: "zh" },
    і: { lat: "i" },
    й: { lat: "y" },
    ў: { lat: "w" },
    х: { lat: "kh" },
    ц: { lat: "ts" },
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    ы: { lat: "y" },
    ь: { lat: BGN_SOFT },
    э: { lat: "e" },
    ю: { lat: "yu" },
    я: { lat: "ya" },
    "’": { lat: `${BGN_SOFT}${BGN_SOFT}` },
  },
  bg: {
    ...COMMON,
    г: { lat: "g" },
    ж: { lat: "zh" },
    и: { lat: "i" },
    й: { lat: "y" },
    х: { lat: "h" },
    ц: { lat: "ts" },
    ч: { lat: "ch" },
    ш: { lat: "sh" },
    щ: { lat: "sht" },
    ъ: { lat: "a" },
    ь: { lat: "y" },
    ю: { lat: "yu" },
    я: { lat: "ya", note: "bg-final-ia" },
  },
};

/** ISO 9:1995 (GOST 7.79-2000 System A, Table 1): one table for every language. */
const ISO9: Table = {
  ...COMMON,
  г: { lat: "g" },
  ґ: { lat: "g\u0300" },
  ё: { lat: "ë" },
  є: { lat: "ê" },
  ж: { lat: "ž" },
  и: { lat: "i" },
  і: { lat: "ì" },
  ї: { lat: "ï" },
  й: { lat: "j" },
  ў: { lat: "ŭ" },
  х: { lat: "h" },
  ц: { lat: "c" },
  ч: { lat: "č" },
  ш: { lat: "š" },
  щ: { lat: "ŝ" },
  ъ: { lat: DOUBLE_PRIME },
  ы: { lat: "y" },
  ь: { lat: PRIME },
  ѣ: { lat: "ě", note: "historical" },
  э: { lat: "è" },
  ю: { lat: "û" },
  я: { lat: "â" },
  ѫ: { lat: "ǎ", note: "historical" },
  ѳ: { lat: "f\u0300", note: "historical" },
  ѵ: { lat: "ỳ", note: "historical" },
  "’": { lat: "’", note: "kept" },
};

const SCHOLARLY_EAST: Table = {
  ...COMMON,
  ж: { lat: "ž" },
  й: { lat: "j" },
  х: { lat: "x" },
  ц: { lat: "c" },
  ч: { lat: "č" },
  ш: { lat: "š" },
  ь: { lat: PRIME },
  ю: { lat: "ju" },
  я: { lat: "ja" },
};

const SCHOLARLY: Record<CyrillicLanguage, Table> = {
  ru: {
    ...SCHOLARLY_EAST,
    г: { lat: "g" },
    ё: { lat: "ë" },
    и: { lat: "i" },
    щ: { lat: "šč" },
    ъ: { lat: DOUBLE_PRIME },
    ы: { lat: "y" },
    э: { lat: "è" },
    і: { lat: "i", note: "historical" },
    ѣ: { lat: "ě", note: "historical" },
    ѳ: { lat: "f", note: "historical" },
    ѵ: { lat: "i", note: "historical" },
  },
  uk: {
    ...SCHOLARLY_EAST,
    г: { lat: "h" },
    ґ: { lat: "g" },
    є: { lat: "je" },
    и: { lat: "y" },
    і: { lat: "i" },
    ї: { lat: "ji" },
    щ: { lat: "šč" },
    "’": { lat: "’", note: "kept" },
  },
  be: {
    ...SCHOLARLY_EAST,
    г: { lat: "h" },
    ґ: { lat: "g", note: "historical" },
    ё: { lat: "ë" },
    і: { lat: "i" },
    ў: { lat: "ŭ" },
    ы: { lat: "y" },
    э: { lat: "è" },
    ѣ: { lat: "ě", note: "historical" },
    "’": { lat: "’", note: "kept" },
  },
  bg: {
    ...SCHOLARLY_EAST,
    г: { lat: "g" },
    и: { lat: "i" },
    щ: { lat: "št" },
    ъ: { lat: "ă" },
    ѣ: { lat: "ě", note: "historical" },
    ѫ: { lat: "ȧ", note: "historical" },
  },
};

/** ICAO Doc 9303 Part 3 §6 B, as applied to Russian. The soft sign is not encoded. */
const ICAO_RU: Table = {
  ...COMMON,
  г: { lat: "g" },
  ё: { lat: "e" },
  ж: { lat: "zh" },
  и: { lat: "i" },
  й: { lat: "i" },
  х: { lat: "kh" },
  ц: { lat: "ts" },
  ч: { lat: "ch" },
  ш: { lat: "sh" },
  щ: { lat: "shch" },
  ъ: { lat: "ie" },
  ы: { lat: "y" },
  ь: { lat: "", note: "dropped" },
  э: { lat: "e" },
  ю: { lat: "iu" },
  я: { lat: "ia" },
  і: { lat: "i", note: "historical" },
  ѵ: { lat: "y", note: "historical" },
};

/** The systems that apply to each language, in the order the widget shows them. */
export const SYSTEMS_FOR: Record<CyrillicLanguage, readonly CyrillicSystem[]> =
  {
    ru: ["ala-lc", "bgn", "iso9", "scholarly", "icao"],
    uk: ["ala-lc", "bgn", "iso9", "scholarly"],
    be: ["ala-lc", "bgn", "iso9", "scholarly"],
    bg: ["ala-lc", "bgn", "iso9", "scholarly"],
  };

export function cyrillicTable(
  language: CyrillicLanguage,
  system: CyrillicSystem
): Table {
  switch (system) {
    case "ala-lc":
      return ALA_LC[language];
    case "bgn":
      return BGN[language];
    case "iso9":
      return ISO9;
    case "scholarly":
      return SCHOLARLY[language];
    default:
      return ICAO_RU;
  }
}

/** The alphabet in dictionary order, then the letters that only old spellings use. */
export const ALPHABETS: Record<CyrillicLanguage, readonly string[]> = {
  ru: [..."абвгдеёжзийклмнопрстуфхцчшщъыьэюя", ..."іѣѳѵ"],
  uk: [..."абвгґдеєжзиіїйклмнопрстуфхцчшщьюя", "’"],
  be: [..."абвгдеёжзійклмнопрстуўфхцчшыьэюя", "’", ..."ґиїщъѣ"],
  bg: [..."абвгдежзийклмнопрстуфхцчшщъьюя", ..."ѣѫ"],
};

const APOSTROPHES = /['ʼ’]/g;
const STRESS_MARKS = /[\u0300\u0301]/g;
const CYRILLIC_LETTER = /[Ѐ-ӿ]/;
const RU_YE_AFTER = new Set([..."аеёиоуыэюяйъь"]);

function isLetter(char: string | undefined): boolean {
  return char !== undefined && (CYRILLIC_LETTER.test(char) || char === "’");
}

function isUpper(char: string): boolean {
  return char !== char.toLowerCase() && char === char.toUpperCase();
}

interface Context {
  word: readonly string[];
  index: number;
  lower: string;
}

interface Position {
  lower: string;
  prev: string | undefined;
  initial: boolean;
  final: boolean;
}

/** е and ё are iotated at the start of a word and after a vowel, й, ъ or ь (notes 1–2). */
function bgnRussian({ lower, prev, initial }: Position): string | null {
  if (lower !== "е" && lower !== "ё") {
    return null;
  }
  if (initial || (prev !== undefined && RU_YE_AFTER.has(prev))) {
    return lower === "е" ? "ye" : "yë";
  }
  return null;
}

/** Final "ия" is "ia": София → Sofia (note 1). */
function bgnBulgarian({ lower, prev, final }: Position): string | null {
  return lower === "я" && prev === "и" && final ? "a" : null;
}

/** ъ is a vowel (ŭ) except at the end of a word in pre-1945 spelling (note 1). */
function alaBulgarian({ lower, final }: Position): string | null {
  return lower === "ъ" && final ? DOUBLE_PRIME : null;
}

const CONTEXT_RULES: Partial<
  Record<
    `${CyrillicSystem}:${CyrillicLanguage}`,
    (position: Position) => string | null
  >
> = {
  "bgn:ru": bgnRussian,
  "bgn:uk": bgnUkrainian,
  "bgn:bg": bgnBulgarian,
  "ala-lc:bg": alaBulgarian,
};

/** Position-dependent forms, or null to use the table's plain value. */
function contextual(
  language: CyrillicLanguage,
  system: CyrillicSystem,
  ctx: Context
): string | null {
  const rule = CONTEXT_RULES[`${system}:${language}`];
  if (!rule) {
    return null;
  }
  const { word, index, lower } = ctx;
  return rule({
    lower,
    prev: index > 0 ? word[index - 1].toLowerCase() : undefined,
    initial: index === 0,
    final: index === word.length - 1,
  });
}

const UKRAINIAN_INITIAL: Record<string, string> = {
  є: "ye",
  ї: "yi",
  й: "y",
  ю: "yu",
  я: "ya",
};

function bgnUkrainian({ lower, initial, prev }: Position): string | null {
  if (initial && lower in UKRAINIAN_INITIAL) {
    return UKRAINIAN_INITIAL[lower];
  }
  // зг is zgh, so it cannot be read as ж (zh): Згорани → Zghorany.
  if (lower === "г" && prev === "з") {
    return "gh";
  }
  return null;
}

/** Upper-case a romanization the way the word around it is written. */
function applyCase(
  romanized: string,
  entry: Entry | undefined,
  upperSource: boolean,
  allCaps: boolean
): string {
  if (!upperSource || romanized === "") {
    return romanized;
  }
  if (allCaps) {
    return romanized.toUpperCase();
  }
  if (entry?.upper && romanized === entry.lat) {
    return entry.upper;
  }
  return romanized.charAt(0).toUpperCase() + romanized.slice(1);
}

/** Bulgarian's one documented exception: the name of the state. */
function bulgarianException(word: string): string | null {
  if (word.toLowerCase() !== "българия") {
    return null;
  }
  if (word === word.toUpperCase()) {
    return "BULGARIA";
  }
  return word[0] === "Б" ? "Bulgaria" : "bulgaria";
}

export interface CyrillicResult {
  text: string;
  /** Letters the chosen table has no value for, left unchanged. */
  unknown: string[];
}

function transliterateWord(
  word: readonly string[],
  language: CyrillicLanguage,
  system: CyrillicSystem,
  unknown: Set<string>
): string {
  if (system === "bgn" && language === "bg") {
    const exception = bulgarianException(word.join(""));
    if (exception) {
      return exception;
    }
  }
  const table = cyrillicTable(language, system);
  const letters = word.filter((char) => char !== "’");
  const allCaps = letters.length > 1 && letters.every(isUpper);
  let out = "";
  for (let index = 0; index < word.length; index++) {
    const char = word[index];
    const lower = char.toLowerCase();
    const entry = table[lower];
    if (!entry) {
      if (char !== "’") {
        unknown.add(lower);
      }
      out += char;
      continue;
    }
    const romanized =
      contextual(language, system, { word, index, lower }) ?? entry.lat;
    out += applyCase(romanized, entry, isUpper(char), allCaps);
  }
  return out;
}

/** Split text into Cyrillic words (with internal apostrophes) and everything between them. */
function* segments(
  text: string
): Generator<{ word: boolean; chars: string[] }> {
  const chars = [...text];
  let index = 0;
  while (index < chars.length) {
    const start = index;
    const isWord = CYRILLIC_LETTER.test(chars[index]);
    if (isWord) {
      while (
        index < chars.length &&
        (CYRILLIC_LETTER.test(chars[index]) ||
          (chars[index] === "’" && isLetter(chars[index + 1])))
      ) {
        index += 1;
      }
    } else {
      while (index < chars.length && !CYRILLIC_LETTER.test(chars[index])) {
        index += 1;
      }
    }
    yield { word: isWord, chars: chars.slice(start, index) };
  }
}

/** Normalise what people paste: composed letters, one apostrophe, no stress marks. */
export function normalizeCyrillic(text: string): string {
  return text
    .normalize("NFD")
    .replace(STRESS_MARKS, "")
    .normalize("NFC")
    .replace(APOSTROPHES, "’");
}

export function transliterateCyrillic(
  text: string,
  language: CyrillicLanguage,
  system: CyrillicSystem
): CyrillicResult {
  const unknown = new Set<string>();
  let out = "";
  for (const segment of segments(normalizeCyrillic(text))) {
    out += segment.word
      ? transliterateWord(segment.chars, language, system, unknown)
      : segment.chars.join("");
  }
  return { text: out, unknown: [...unknown] };
}

/* ------------------------------------------------------------ ISO 9 back to Cyrillic */

/** Latin → Cyrillic for ISO 9, longest match first (g̀ and f̀ are two code points). */
const ISO9_REVERSE: [string, string][] = Object.entries(ISO9)
  .filter(([cyrillic]) => cyrillic !== "’")
  .map(([cyrillic, entry]): [string, string] => [
    entry.lat.normalize("NFC"),
    cyrillic,
  ])
  .sort((a, b) => b[0].length - a[0].length);

export function iso9ToCyrillic(text: string): string {
  const source = text.normalize("NFC").replace(APOSTROPHES, "’");
  let out = "";
  let index = 0;
  while (index < source.length) {
    const rest = source.slice(index);
    const match = ISO9_REVERSE.find(
      ([latin]) => rest.slice(0, latin.length).toLowerCase() === latin
    );
    if (!match) {
      out += source[index];
      index += 1;
      continue;
    }
    const [latin, cyrillic] = match;
    const original = rest.slice(0, latin.length);
    out += isUpper(original.charAt(0)) ? cyrillic.toUpperCase() : cyrillic;
    index += latin.length;
  }
  return out;
}

/* ------------------------------------------------------------------- letter tables */

export interface LetterRow {
  cyrillic: string;
  latin: string;
  note?: CyrillicNote;
  /** The word-initial form, for the `initial-y` note. */
  noteValue?: string;
}

function latinPair(entry: Entry): string {
  if (entry.lat === "") {
    return "—";
  }
  const upper =
    entry.upper ?? entry.lat.charAt(0).toUpperCase() + entry.lat.slice(1);
  return upper === entry.lat ? entry.lat : `${upper} ${entry.lat}`;
}

/** The chosen system's value for every letter of the language, in alphabet order. */
export function letterRows(
  language: CyrillicLanguage,
  system: CyrillicSystem
): LetterRow[] {
  const table = cyrillicTable(language, system);
  const rows: LetterRow[] = [];
  for (const letter of ALPHABETS[language]) {
    const entry = table[letter];
    if (!entry) {
      continue;
    }
    rows.push({
      cyrillic: letter === "’" ? letter : `${letter.toUpperCase()} ${letter}`,
      latin: latinPair(entry),
      note: entry.note,
      noteValue:
        entry.note === "initial-y" ? UKRAINIAN_INITIAL[letter] : undefined,
    });
  }
  return rows;
}
